import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { PopoverView } from "@/components/atoms/popover";
import { QueueTemplateDto } from "@/repository/models/templates";
import {
	fetchVoiceQueueTemplates,
	VoiceQueueTemplatesKey,
} from "@/repository/queryFns/messagingQueueTemplates";
import {
	Button,
	MenuItem,
	MenuList,
	PopoverProps,
} from "@fluentui/react-components";
import { MailTemplate20Regular } from "@fluentui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useStyle } from "./style";
import { OperatorTemplatesProps } from "./types";
import {
	SingleSendEmailActions,
	useSingleSendEmailStore,
} from "@/common/stores/nonpersistingStores/singleSendEmail";
import { useIntl } from "react-intl";
import { ContactType } from "@/repository/models";

export const OperatorTemplates = (props: OperatorTemplatesProps) => {
	const { contact } = props;

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);
	const queryKey = [VoiceQueueTemplatesKey];

	const fetchVoiceQueueTemplatesCallBack = useCallback(
		(_: any) => {
			return fetchVoiceQueueTemplates(currentInteraction?.queueId);
		},
		[queryKey]
	);

	const { data: templates } = useQuery<QueueTemplateDto[]>({
		queryKey: queryKey,
		queryFn: fetchVoiceQueueTemplatesCallBack,
		refetchOnMount: "always",
	});

	const classes = useStyle();

	const intl = useIntl();

	const setSingleEditEmail = useSingleSendEmailStore(
		(actions: SingleSendEmailActions) => actions.update
	);

	const [isMainMenuOpened, setIsMainMenuOpened] = useState<boolean>(false);
	const handleOpenChange: PopoverProps["onOpenChange"] = (_, data) =>
		setIsMainMenuOpened(data.open || false);

	const onTemplateClick = (template: QueueTemplateDto) => {
		if (currentInteraction) {
			setSingleEditEmail({
				template: template,
				recipient: { name: contact.name, address: contact.email },
				interactionId: currentInteraction.id,
			});
			setIsMainMenuOpened(false);
		}
	};

	useEffect(() => {
		return () => {
			setSingleEditEmail(null);
		};
	}, [currentInteraction]);

	const templateItems = useMemo<JSX.Element[]>(
		() =>
			templates
				? templates.map((x: QueueTemplateDto) => (
						<MenuItem
							key={`queue-template-${x.id}`}
							title={x.templateName}
							onClick={() => onTemplateClick(x)}
							children={x.templateName}
							className={classes.operatorTemplatesMenuItem}
						/>
					))
				: [],
		[templates]
	);

	const isButtonDisabled = useMemo<boolean>(() => {
		return (
			!contact.email || contact.contactType != ContactType.AzureActiveDirectory
		);
	}, [contact]);

	return (
		<>
			{templates && templates.length > 0 && (
				<PopoverView
					trigger={
						<Button
							title={
								isButtonDisabled
									? intl.formatMessage({
											id: "Interaction.EmailingContactDisabled",
										})
									: intl.formatMessage({ id: "Interaction.EmailTheAgent" })
							}
							shape="circular"
							appearance="subtle"
							disabled={isButtonDisabled}
							icon={<MailTemplate20Regular />}
							className={classes.button}
							onClick={() => {
								setIsMainMenuOpened((oldState: boolean) => !oldState);
							}}
						/>
					}
					content={
						<MenuList
							children={templateItems}
							className={classes.operatorTemplatesMenu}
						/>
					}
					positioning={{ position: "below", align: "top" }}
					open={isMainMenuOpened}
					onOpenChange={handleOpenChange}
				/>
			)}
		</>
	);
};
